import PropTypes from "prop-types";
import React, { useState, useEffect, memo } from "react";
import {
  handleButtonClickUserAddressHistory,
  submitFormChangeUserName,
} from "../../utils/userProfile";
import { URLs } from "../../utils/apis";
import { decodeHTML } from "../../utils/decodeHTML";
import { useForm } from "@mantine/form";
import {
  TextInput,
  Button,
  Flex,
  Box,
  CopyButton,
  Tooltip,
  ActionIcon,
  Modal,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconCopy } from "@tabler/icons-react";
import {
  formatTimeStamp,
  SHORT_DISPLAY_FORMAT,
} from "../../utils/collectionFound/dateTimeUtils";
import TipTapEditor from "./TipTapEditor";
import DOMPurify from "dompurify";
import { useStylesUserInputBlock } from "../../utils/mantine/useStylesUserInputBlock";
import CustomButton from "../mantineCustomComponents/CustomButton";
import { useStylesIcons } from "../../utils/mantine/useStylesIcons";
import { useStylesCollectionWindow } from "../../utils/mantine/useStylesCollectionWindow";
// Media query to detect if the screen width is less than 64em (1024px)

const UserInfo = ({
  setUserAddressHistoryData,
  setUserDelete,
  setChangePassword,
  setChangedUserName,
  changedUserName,
  timeZone,
}) => {
  const tabletMediaQuery = useMediaQuery("(max-width: 64em)");

  const mobileMediaQuery = useMediaQuery("(max-width: 31.5em)");

  // Mantine theme instance to apply custom styles and theming throughout the component
  const theme = useMantineTheme();

  // Use custom styles for the TipTap editor
  const { classes: userInputBlockClasses } = useStylesUserInputBlock();

  const { classes: classesIcons } = useStylesIcons();
  const { classes: collectionWindowClasses } = useStylesCollectionWindow();

  const [enableName, setEnableName] = useState(false);

  const [enableAbout, setEnableAbout] = useState(false);

  const [initialName, setInitialName] = useState("");
  const [isVerifiedUser, setIsVerifiedUser] = useState(false);
  const [openedNameConfirmModal, setOpenedNameConfirmModal] = useState(false);

  const stripHtml = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      name: "",
      email: "",
      last_address: "",
      date_joined: "",
      last_is_verified: "Unverified",
      description: "",
    },
    enhanceGetInputProps: (payload) => {
      if (
        (enableName && payload.field === "name") ||
        (enableAbout && payload.field === "description")
      ) {
        return { disabled: false };
      }
      return { disabled: true };
    },
    // functions will be used to validate values at corresponding key
    validate: {
      name: (value) => {
        if (!enableName) return null;
        if (value.length < 3) {
          return "Input must be at least 3 characters";
        }
        if (value.length > 70) {
          return "Input must be less than 70 characters";
        }

        const regex = /^[\w\s!@#$%&*()_\-+=.,'":;?]+$/;
        if (!regex.test(value)) {
          return "Input contains invalid characters. Allowed characters: ! @ # $ % & * ( ) _ - + = . , ' \" : ; ?";
        }
        return null;
      },

      description: (value) => {
        if (!enableAbout) return null;

        const cleanText = stripHtml(DOMPurify.sanitize(decodeHTML(value)));

        if (cleanText.length > 2600) {
          return "Input must be less than 2600 characters";
        }
        if (cleanText.length < 3) {
          return "Input must be at least 3 characters";
        }

        const regex = /^[\w\s!@#$%&*()_\-+=.,'":;?/]+$/;
        if (!regex.test(cleanText)) {
          return "Input contains invalid characters. Allowed characters: ! @ # $ % & * ( ) _ - + = . , ' \" : ; ? /";
        }

        return null;
      },
    },
  });

  // Temporarily hide items in development
  const [hideInProgress, setHideInProgress] = useState(true); // eslint-disable-line no-unused-vars

  const [isLoading, setIsLoading] = useState(true); // Loading state

  const isNameChanged = (value) => value.trim() !== initialName.trim();

  const submitProfileUpdate = async (values, field) => {
    try {
      const wasSaved = await submitFormChangeUserName(
        values,
        field,
        form,
        setEnableName,
        setEnableAbout,
        setChangedUserName,
      );
      if (wasSaved && field === "name") {
        setInitialName(values.name || "");
        setIsVerifiedUser(false);
        form.setFieldValue("last_is_verified", "Unverified");
      }
    } catch (error) {
      console.error("Submission failed:", error);
    }
  };

  const handleNameSaveAttempt = async () => {
    const validation = form.validateField("name");
    if (validation.hasError) {
      return;
    }

    if (isVerifiedUser && isNameChanged(form.getValues().name || "")) {
      setOpenedNameConfirmModal(true);
      return;
    }

    await submitProfileUpdate(form.getValues(), "name");
  };

  const handleAboutSaveAttempt = async () => {
    const validation = form.validateField("description");
    if (validation.hasError) {
      return;
    }

    await submitProfileUpdate(form.getValues(), "description");
  };

  // Fetch user data from API
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await fetch(URLs.USER_INFO_URL);
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }

        const data = await response.json();
        const modifiedData = {
          ...data,
          last_is_verified: data.last_is_verified ? "Verified" : "Unverified",
          date_joined: formatTimeStamp(
            data.date_joined,
            timeZone,
            SHORT_DISPLAY_FORMAT,
          ),
        };
        form.initialize(modifiedData);
        setInitialName(data.name || "");
        setIsVerifiedUser(Boolean(data.last_is_verified));
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [changedUserName]); // Updates after the Username changes

  const handleButtonClickUserDelete = () => {
    setUserDelete(true);
  };

  const handleButtonClickChangePassword = () => {
    setChangePassword(true);
  };

  const handleUserInfoSubmit = async (event) => {
    event.preventDefault();

    if (enableName) {
      await handleNameSaveAttempt();
      return;
    }

    if (enableAbout) {
      await handleAboutSaveAttempt();
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Flex
          align={theme.flex.align.center}
          justify={theme.flex.justify.center}
          styles={() => ({
            root: {
              minHeight: 400,
            },
          })}
        >
          Loading...
        </Flex>
      </Box>
    ); // Show a loading message while data is being fetched
  }
  return (
    <Box
      className="user-info"
      style={() => ({
        paddingTop: 83,
        paddingBottom: 100,
      })}
    >
      <form onSubmit={handleUserInfoSubmit}>
        <Flex
          classNames={{
            root: "user-name-block",
          }}
          gap={theme.flex.gap.large}
          mb={theme.margin.xxl}
          align={theme.flex.align.center}
          wrap={tabletMediaQuery ? "wrap" : "nowrap"}
          justify={tabletMediaQuery ? "flex-start" : "center"}
        >
          <TextInput
            styles={(theme) => ({
              root: {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: tabletMediaQuery ? "flex-start" : "center",
                maxWidth: "100%",
              },
              label: {
                paddingRight: 20,
                fontFamily: theme.headings.fontFamily.bold,
                fontSize: "1.2em",
                width: 236,
                textAlign: tabletMediaQuery ? "left" : "right",
              },
              input: {
                paddingRight: 20,
                border: "1px solid rgba(112, 112, 112, 0.5019607843)",
                fontSize: "1rem",
                lineHeight: "1.25rem",
                maxWidth: "100%",
                width: tabletMediaQuery ? "100%" : "22rem",
              },
            })}
            label="Display Name:"
            inputWrapperOrder={["label", "input"]}
            inputContainer={(children) => (
              <Box>
                {children}
                <Box
                  style={(theme) => ({
                    fontSize: "0.93em",
                    lineHeight: "0.93em",
                    position: "absolute",
                    color: theme.customColors.red,
                    fontFamily: theme.headings.fontFamily.light,
                    width: "23rem",
                  })}
                >
                  {form.errors.name}
                </Box>
              </Box>
            )}
            placeholder="Name"
            key={form.key("name")}
            {...form.getInputProps("name")}
          />

          {enableName ? (
            <CustomButton
              text="Save"
              componentClasses="link linkWidth90"
              type="submit"
              variant="transparent"
            />
          ) : (
            <CustomButton
              text="Change"
              componentClasses="link linkWidth90"
              type="button"
              variant="transparent"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setEnableName((prev) => !prev);
                setEnableAbout(false);
              }}
            />
          )}
        </Flex>

        <Flex
          classNames={{
            root: "user-about-block " + userInputBlockClasses.userInputBlock,
          }}
          gap={20}
          mb={theme.margin.xxl}
          align={theme.flex.align.center}
          wrap={tabletMediaQuery ? "wrap" : "nowrap"}
          justify={tabletMediaQuery ? "flex-start" : "center"}
        >
          <Flex
            align={theme.flex.align.center}
            justify={theme.flex.justify.center}
            wrap="wrap"
          >
            <label
              style={{
                paddingRight: 20,
                fontFamily: theme.headings.fontFamily.bold,
                fontSize: "1.2em",
                width: 236,
                textAlign: tabletMediaQuery ? "left" : "right",
              }}
            >
              About:{" "}
            </label>
            <TipTapEditor
              placeholder="About"
              value={form.getValues().description || ""}
              onChange={(value) => form.setFieldValue("description", value)}
              error={form.errors.description || ""}
              onEnableAbout={setEnableAbout}
            />
          </Flex>

          <CustomButton
            text="Save"
            componentClasses="link linkWidth90"
            type="button"
            variant="transparent"
            onClick={async () => {
              setEnableName(false);
              setEnableAbout(true);
              await handleAboutSaveAttempt();
            }}
          />
        </Flex>

        <Flex
          gap={20}
          mb={theme.margin.xxl}
          align={theme.flex.align.center}
          wrap={tabletMediaQuery ? "wrap" : "nowrap"}
          justify={tabletMediaQuery ? "flex-start" : "center"}
        >
          <TextInput
            styles={(theme) => ({
              root: {
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                justifyContent: tabletMediaQuery ? "flex-start" : "center",
              },
              label: {
                paddingRight: 20,
                fontFamily: theme.headings.fontFamily.bold,
                fontSize: "1.2em",
                width: 236,
                textAlign: tabletMediaQuery ? "left" : "right",
              },
              input: {
                paddingRight: 20,
                border: "1px solid rgba(112, 112, 112, 0.5019607843)",
                fontSize: "1rem",
                lineHeight: "1.25rem",
                maxWidth: "100%",
                width: tabletMediaQuery ? "100%" : "22rem",
              },
            })}
            label="E-mail Address:"
            placeholder="Email"
            key={form.key("email")}
            {...form.getInputProps("email")}
          />
          <Button
            type="button"
            variant="transparent"
            color="#000"
            styles={() => ({
              root: {
                fontSize: "1.2em",
                textDecoration: "underline",
                paddingLeft: "0",
                width: 90,
                cursor: "default",
                textAlign: "left",
              },
            })}
          ></Button>
        </Flex>

        <Flex
          gap={20}
          mb={theme.margin.xxl}
          align={theme.flex.align.center}
          wrap={tabletMediaQuery ? "wrap" : "nowrap"}
          justify={tabletMediaQuery ? "flex-start" : "center"}
          className={userInputBlockClasses.wrap}
          style={() => ({
            paddingLeft: tabletMediaQuery ? "0" : "5em",
            display: tabletMediaQuery ? "inline-block" : "",
          })}
        >
          <Box
            style={() => ({
              position: "relative",
            })}
          >
            <TextInput
              styles={(theme) => ({
                root: {
                  display: mobileMediaQuery ? "block" : "flex",
                  flexWrap: "wrap",
                  justifyContent: tabletMediaQuery ? "flex-start" : "center",
                  alignItems: "center",
                },
                label: {
                  paddingRight: 20,
                  fontFamily: theme.headings.fontFamily.bold,
                  fontSize: "1.2em",
                  width: 236,
                  textAlign: tabletMediaQuery ? "left" : "right",
                },
                input: {
                  paddingRight: 20,
                  border: "1px solid rgba(112, 112, 112, 0.5019607843)",
                  fontSize: "1rem",
                  lineHeight: "1.25rem",
                  maxWidth: "100%",
                  width: tabletMediaQuery ? "100%" : "22rem",
                },
              })}
              label="Current User Address:"
              placeholder="Current User Address:"
              key={form.key("last_address")}
              {...form.getInputProps("last_address")}
            />

            <CopyButton value={form.getValues().last_address} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied" : "Copy"}
                  className={classesIcons.copy}
                  withArrow
                  position="right"
                >
                  <ActionIcon
                    color={
                      copied
                        ? theme.customColors.actionIconFirst
                        : theme.customColors.actionIconSecond
                    }
                    variant="subtle"
                    onClick={copy}
                    style={() => ({
                      position: "absolute",
                      top: mobileMediaQuery ? "2.15em" : "0.3em",
                      right: "-2.7em",
                    })}
                  >
                    {copied ? (
                      <IconCheck className={classesIcons.width} />
                    ) : (
                      <IconCopy className={classesIcons.width} />
                    )}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Box>

          <CustomButton
            text="User Address History"
            componentClasses="link linkUserAddressCorrect"
            type="button"
            variant="transparent"
            onClick={() =>
              handleButtonClickUserAddressHistory(setUserAddressHistoryData)
            }
          />
        </Flex>

        <Flex
          gap={20}
          mb={theme.margin.xxl}
          align={theme.flex.align.center}
          wrap={tabletMediaQuery ? "wrap" : "nowrap"}
          className={userInputBlockClasses.wrap}
          justify={tabletMediaQuery ? "flex-start" : "center"}
          style={() => ({
            paddingLeft: tabletMediaQuery ? "0" : "2em",
            display: tabletMediaQuery ? "inline-block" : "",
          })}
        >
          <Box
            style={() => ({
              position: "relative",
            })}
          >
            <TextInput
              styles={(theme) => ({
                root: {
                  display: mobileMediaQuery ? "block" : "flex",
                  flexWrap: "wrap",
                  justifyContent: tabletMediaQuery ? "flex-start" : "center",
                  alignItems: "center",
                },
                label: {
                  paddingRight: 20,
                  fontFamily: theme.headings.fontFamily.bold,
                  fontSize: "1.2em",
                  width: 236,
                  textAlign: tabletMediaQuery ? "left" : "right",
                },
                input: {
                  paddingRight: 20,
                  border: "1px solid rgba(112, 112, 112, 0.5019607843)",
                  fontSize: "1rem",
                  lineHeight: "1.25rem",
                  maxWidth: "100%",
                  width: tabletMediaQuery ? "100%" : "22rem",
                },
              })}
              label="Persistent ID:"
              placeholder="Persistent ID:"
              key={form.key("persistent_id")}
              {...form.getInputProps("persistent_id")}
            />

            <CopyButton value={form.getValues().persistent_id} timeout={2000}>
              {({ copied, copy }) => (
                <Tooltip
                  label={copied ? "Copied" : "Copy"}
                  className={classesIcons.copy}
                  withArrow
                  position="right"
                >
                  <ActionIcon
                    color={
                      copied
                        ? theme.customColors.actionIconFirst
                        : theme.customColors.actionIconSecond
                    }
                    variant="subtle"
                    onClick={copy}
                    style={() => ({
                      position: "absolute",
                      top: mobileMediaQuery ? "2.15em" : "0.3em",
                      right: "-2.7em",
                    })}
                  >
                    {copied ? (
                      <IconCheck className={classesIcons.width} />
                    ) : (
                      <IconCopy className={classesIcons.width} />
                    )}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Box>
          <Button
            type="button"
            variant="transparent"
            color="#000"
            styles={() => ({
              root: {
                fontSize: "1.2em",
                textDecoration: "underline",
                paddingLeft: "0",
                width: "6.7em",
                cursor: "default",
                textAlign: "left",
                zIndex: "-1",
              },
            })}
          ></Button>
        </Flex>

        <Flex
          gap={theme.flex.gap.large}
          mb={theme.margin.xxl}
          align={theme.flex.align.center}
          wrap={tabletMediaQuery ? "wrap" : "nowrap"}
          justify={tabletMediaQuery ? "flex-start" : "center"}
        >
          <TextInput
            styles={(theme) => ({
              root: {
                display: "flex",
                flexWrap: "wrap",
                justifyContent: tabletMediaQuery ? "flex-start" : "center",
                alignItems: "center",
              },
              label: {
                paddingRight: 20,
                fontFamily: theme.headings.fontFamily.bold,
                fontSize: "1.2em",
                width: 236,
                textAlign: tabletMediaQuery ? "left" : "right",
              },
              input: {
                paddingRight: 20,
                border: "1px solid rgba(112, 112, 112, 0.5019607843)",
                fontSize: "1rem",
                lineHeight: "1.25rem",
                maxWidth: "100%",
                width: tabletMediaQuery ? "100%" : "22rem",
              },
            })}
            label="Identity Verification Status:"
            placeholder="Identity Verification Status:"
            key={form.key("last_is_verified")}
            {...form.getInputProps("last_is_verified")}
          />
          <Button
            type="button"
            variant="transparent"
            color="#000"
            styles={() => ({
              root: {
                fontSize: "1.2em",
                textDecoration: "underline",
                paddingLeft: "0",
                width: 90,
                cursor: "default",
                textAlign: "left",
              },
            })}
          ></Button>
        </Flex>

        <Flex
          gap={20}
          mb={theme.margin.xxl}
          align={theme.flex.align.center}
          wrap={tabletMediaQuery ? "wrap" : "nowrap"}
          justify={tabletMediaQuery ? "flex-start" : "center"}
        >
          <TextInput
            styles={(theme) => ({
              root: {
                display: "flex",
                flexWrap: "wrap",
                justifyContent: tabletMediaQuery ? "flex-start" : "center",
                alignItems: "center",
              },
              label: {
                paddingRight: 20,
                fontFamily: theme.headings.fontFamily.bold,
                fontSize: "1.2em",
                width: 236,
                textAlign: tabletMediaQuery ? "left" : "right",
              },
              input: {
                paddingRight: 20,
                border: "1px solid rgba(112, 112, 112, 0.5019607843)",
                fontSize: "1rem",
                lineHeight: "1.25rem",
                maxWidth: "100%",
                width: tabletMediaQuery ? "100%" : "22rem",
              },
            })}
            label="Account Created on Date:"
            placeholder="Account Created on Date:"
            key={form.key("date_joined")}
            {...form.getInputProps("date_joined")}
          />
          <Button
            type="button"
            variant="transparent"
            color="#000"
            styles={() => ({
              root: {
                fontSize: "1.2em",
                textDecoration: "underline",
                paddingLeft: "0",
                width: 90,
                cursor: "default",
                textAlign: "left",
              },
            })}
          ></Button>
        </Flex>
      </form>

      <Modal
        opened={openedNameConfirmModal}
        onClose={() => setOpenedNameConfirmModal(false)}
        centered
        size="30rem"
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        className={collectionWindowClasses.collectionWindow}
      >
        <Text className="text">
          Changing your Display Name will reset your verified status and you
          will need to verify your identity again.
        </Text>

        <Flex justify={theme.flex.justify.center}>
          <Text
            classNames={{
              root: "confirmation-title",
            }}
          >
            Change Display Name?
          </Text>
        </Flex>

        <Flex
          justify={theme.flex.justify.center}
          gap={theme.flex.gap.large}
          mb={theme.margin.xl}
        >
          <CustomButton
            text="Cancel"
            componentClasses="buttonWhite"
            type="button"
            onClick={() => setOpenedNameConfirmModal(false)}
          />
          <CustomButton
            text="Yes"
            componentClasses="buttonBlack modal-button-submit"
            type="button"
            onClick={async () => {
              setOpenedNameConfirmModal(false);
              await submitProfileUpdate(form.getValues(), "name");
            }}
          />
        </Flex>
      </Modal>

      {!hideInProgress && (
        <>
          <Flex
            gap={theme.flex.gap.large}
            mb={theme.margin.xxl}
            align={theme.flex.align.center}
            justify={theme.flex.justify.center}
          >
            <Button
              type="button"
              variant="transparent"
              color="#000"
              styles={(theme) => ({
                root: {
                  fontSize: "1.2em",
                  textDecoration: "underline",
                  paddingLeft: "0",
                  cursor: "pointer",
                  overflow: "unset",
                  marginTop: 20,
                  fontFamily: theme.headings.fontFamily.light,
                },
              })}
              onClick={() => handleButtonClickChangePassword()}
            >
              Change Password
            </Button>
          </Flex>

          <Flex
            gap={theme.flex.gap.large}
            mb={theme.margin.xxl}
            align={theme.flex.align.center}
            justify={theme.flex.justify.center}
          >
            <Button
              type="button"
              variant="transparent"
              color="#000"
              styles={(theme) => ({
                root: {
                  fontSize: "1.2em",
                  textDecoration: "underline",
                  paddingLeft: "0",
                  cursor: "pointer",
                  overflow: "unset",
                  marginTop: 20,
                  fontFamily: theme.headings.fontFamily.light,
                  color: theme.customColors.red,
                },
              })}
              onClick={() => handleButtonClickUserDelete()}
            >
              Cancel Account
            </Button>
          </Flex>
        </>
      )}
    </Box>
  );
};

UserInfo.propTypes = {
  setUserAddressHistoryData: PropTypes.func.isRequired,
  setUserDelete: PropTypes.func,
  setChangePassword: PropTypes.func,
  setChangedUserName: PropTypes.func,
  changedUserName: PropTypes.bool,
  timeZone: PropTypes.string.isRequired,
};

export default memo(UserInfo);
